import type React from 'react';
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { adminEmailsApi } from '@api/admin-extras';
import { ApiRequestError } from '@api/client';
import type { EmailSettingDto, EmailType } from '@api/types';
import { EmailPreviewModal } from './emails/EmailPreviewModal';

/**
 * Section "Emails" del Settings superadmin (PR #8).
 *
 * Lista los 8 tipos transaccionales con su master switch + un botón "Vista
 * previa" que abre el modal de render. `magic_link` SIEMPRE aparece como
 * switch ON deshabilitado — el backend devuelve 422 con code
 * `MAGIC_LINK_NOT_TOGGLEABLE` si se fuerza un PUT.
 */

interface EmailRowMeta {
  type: EmailType;
  label: string;
  desc: string;
}

/**
 * Orden de visualización en la lista. Los magic_link va al final como
 * "esencial" para que se lea como "default ON, no se toca".
 */
const ROWS: readonly EmailRowMeta[] = [
  {
    type: 'booking_confirmation',
    label: 'Confirmación de reserva (auto)',
    desc: 'Email de confirmación inmediata cuando el centro tiene modo automático.',
  },
  {
    type: 'booking_pending_approval',
    label: 'Solicitud pendiente de aprobación',
    desc: 'Avisa al cliente de que su solicitud está en revisión (modo manual).',
  },
  {
    type: 'booking_confirmed',
    label: 'Aprobada por el administrador',
    desc: 'Confirmación enviada cuando el admin acepta una solicitud manual.',
  },
  {
    type: 'booking_rejected',
    label: 'Rechazada por el administrador',
    desc: 'Notificación de rechazo (opcionalmente con motivo).',
  },
  {
    type: 'booking_cancellation',
    label: 'Cancelación',
    desc: 'Email enviado al cancelar una reserva (desde admin o cliente).',
  },
  {
    type: 'booking_rescheduled',
    label: 'Reagendamiento',
    desc: 'Aviso de cambio de fecha/hora con el detalle antes/después.',
  },
  {
    type: 'booking_reminder',
    label: 'Recordatorio (24h / 2h)',
    desc: 'Recordatorios automáticos 24h y 2h antes de la cita.',
  },
  {
    type: 'magic_link',
    label: 'Magic link (login)',
    desc: 'Esencial para el login del admin; no se puede desactivar.',
  },
];

export function EmailsSection(): React.JSX.Element {
  const qc = useQueryClient();
  const [previewType, setPreviewType] = useState<EmailType | null>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const { data, isLoading, isError } = useQuery({
    queryKey: ['email-settings'],
    queryFn: () => adminEmailsApi.settings(),
  });

  const settingsByType = new Map<EmailType, EmailSettingDto>();
  for (const s of data?.items ?? []) settingsByType.set(s.type, s);

  const toggleMutation = useMutation({
    mutationFn: (v: { type: EmailType; enabled: boolean }) =>
      adminEmailsApi.setEnabled(v.type, v.enabled),
    // Optimistic update: aplicamos el cambio en caché antes del servidor para
    // evitar el "salto" del switch. Si falla, rollback con el snapshot previo.
    onMutate: async (v) => {
      setErrorMsg(null);
      await qc.cancelQueries({ queryKey: ['email-settings'] });
      const previous = qc.getQueryData<{ items: EmailSettingDto[] }>(['email-settings']);
      if (previous) {
        qc.setQueryData<{ items: EmailSettingDto[] }>(['email-settings'], {
          items: previous.items.map((it) =>
            it.type === v.type ? { ...it, enabled: v.enabled } : it,
          ),
        });
      }
      return { previous };
    },
    onError: (err, _v, ctx) => {
      if (ctx?.previous) qc.setQueryData(['email-settings'], ctx.previous);
      if (err instanceof ApiRequestError && err.code === 'MAGIC_LINK_NOT_TOGGLEABLE') {
        setErrorMsg('El magic link es esencial para el login y no se puede desactivar.');
      } else {
        setErrorMsg(err instanceof Error ? err.message : 'No se pudo guardar el cambio.');
      }
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: ['email-settings'] });
    },
  });

  function onToggle(type: EmailType, currentEnabled: boolean): void {
    if (type === 'magic_link') return; // defensa extra: la UI ya lo desactiva
    toggleMutation.mutate({ type, enabled: !currentEnabled });
  }

  return (
    <div className="card">
      <div className="card-head">
        <div>
          <h2 className="card-title">Emails</h2>
          <p className="page-sub" style={{ margin: '4px 0 0' }}>
            Controla qué emails envía el sistema y previsualiza cada plantilla.
          </p>
        </div>
      </div>
      <div className="card-body">
        {isLoading && (
          <div style={{ padding: 24, color: 'var(--fg-muted)' }}>
            <span className="spinner" /> Cargando ajustes…
          </div>
        )}

        {isError && (
          <div style={{ padding: 16, color: 'var(--danger)', fontSize: 13 }}>
            No se pudieron cargar los ajustes de email.
          </div>
        )}

        {errorMsg && (
          <div
            role="alert"
            style={{
              padding: '8px 12px',
              marginBottom: 12,
              fontSize: 12.5,
              color: 'var(--danger)',
              background: 'var(--danger-soft, rgba(220,38,38,0.08))',
              border: '1px solid var(--border)',
              borderRadius: 6,
            }}
          >
            {errorMsg}
          </div>
        )}

        {data && (
          <div>
            {ROWS.map((row) => {
              const setting = settingsByType.get(row.type);
              // Default seguro: ausencia de row en BD = enabled. Mantiene el
              // contrato del backend ("trata las rows faltantes como ON").
              const enabled = setting?.enabled ?? true;
              const isMagicLink = row.type === 'magic_link';
              const switchOn = isMagicLink ? true : enabled;

              return (
                <div className="switch-row" key={row.type}>
                  <div className="switch-row-info">
                    <div className="switch-row-name">{row.label}</div>
                    <div className="switch-row-desc">{row.desc}</div>
                  </div>

                  <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                    <button
                      type="button"
                      className="btn"
                      data-variant="ghost"
                      onClick={() => { setPreviewType(row.type); }}
                      style={{ fontSize: 12 }}
                    >
                      Vista previa
                    </button>

                    <div
                      className="switch"
                      data-on={switchOn}
                      onClick={isMagicLink ? undefined : () => { onToggle(row.type, enabled); }}
                      role="switch"
                      aria-checked={switchOn}
                      aria-disabled={isMagicLink}
                      aria-label={`Activar email "${row.label}"`}
                      title={
                        isMagicLink
                          ? 'Esencial para el login, no se puede desactivar'
                          : undefined
                      }
                      style={
                        isMagicLink
                          ? { cursor: 'not-allowed', opacity: 0.6 }
                          : undefined
                      }
                    />
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {previewType && (
        <EmailPreviewModal
          type={previewType}
          label={ROWS.find((r) => r.type === previewType)?.label ?? previewType}
          onClose={() => { setPreviewType(null); }}
        />
      )}
    </div>
  );
}
